import { TableHead, TableHeader, TableRow } from "@screener/uikit/components/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@screener/uikit/components/tooltip";
import { cn } from "@screener/uikit/lib/utils";
import { densityColumnHint, densityColumnLabel, sortKeyLabel } from "@/lib/labels";
import type { DensitySort, DensitySortKey } from "./derive";

/**
 * Шапка колонок таблицы плотностей §9 — общая часть `DensityTable`,
 * `DensityCoinTable` и `SituationList` (design.md change stage6-coin-view,
 * решение 2): состав и порядок колонок один и тот же, разница — показывают ли
 * заголовки текущую сортировку стрелкой. `SituationList` показывает её ровно
 * как `DensityTable`, поэтому несёт `showsSort` вариант этого же компонента, а
 * не свою копию.
 */

interface HeaderColumn {
  label: string;
  /** Колонка показывает текущую сортировку, когда шапка показывает сортировку (`showsSort`). */
  sortKey?: DensitySortKey;
  /** Полное слово для сокращённого заголовка — подсказка при наведении. */
  title?: string;
  className: string;
}

const HEADER_COLUMNS: HeaderColumn[] = [
  { label: densityColumnLabel("venue"), className: "text-left" },
  { label: densityColumnLabel("pair"), className: "text-left" },
  { label: densityColumnLabel("side"), className: "text-left" },
  { label: densityColumnLabel("anchor"), className: "text-right" },
  { label: sortKeyLabel("distance"), sortKey: "distance", className: "text-right" },
  { label: sortKeyLabel("usd"), sortKey: "usd", className: "text-right" },
  {
    label: densityColumnLabel("levels"),
    title: densityColumnHint("levels"),
    className: "text-right",
  },
  { label: sortKeyLabel("age"), sortKey: "age", className: "text-right" },
  { label: densityColumnLabel("state"), className: "text-left" },
  {
    label: densityColumnLabel("approaches"),
    title: densityColumnHint("approaches"),
    className: "text-right",
  },
  { label: densityColumnLabel("flags"), className: "text-left" },
];

function sortMark(sort: DensitySort, key: DensitySortKey): string {
  if (sort.key !== key) {
    return "";
  }
  return sort.direction === "asc" ? " ↑" : " ↓";
}

/**
 * Заголовок сортируемой колонки — обычная ячейка: стрелка текущего
 * направления и `aria-sort` только показывают порядок, нажатием он не
 * меняется (1.51, §64 п. 2: сортировкой управляет переключатель панели
 * фильтров, два органа на одно действие вели бы себя в видах по-разному).
 */
function SortableHead({
  label,
  sortKey,
  sort,
}: {
  label: string;
  sortKey: DensitySortKey;
  sort: DensitySort;
}) {
  const active = sort.key === sortKey;
  return (
    <TableHead
      aria-sort={active ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}
      className={cn(
        "h-6 px-2 text-right text-[11px] font-medium tracking-wider uppercase",
        active ? "text-foreground" : "text-muted-foreground",
      )}
    >
      {label}
      {sortMark(sort, sortKey)}
    </TableHead>
  );
}

function PlainHead({ column }: { column: HeaderColumn }) {
  return (
    <TableHead
      className={cn(
        "h-6 px-2 text-[11px] font-medium tracking-wider text-muted-foreground uppercase",
        column.className,
      )}
    >
      {/* Полное слово сокращения — `<abbr>`, а не только `title` на `<th>`:
          семантическая разметка сокращения доступна читалке экрана и
          клавиатуре, не только наведению мыши. */}
      {column.title !== undefined ? (
        <Tooltip>
          <TooltipTrigger render={<abbr className="no-underline" />}>{column.label}</TooltipTrigger>
          <TooltipContent>{column.title}</TooltipContent>
        </Tooltip>
      ) : (
        column.label
      )}
    </TableHead>
  );
}

/**
 * Шапка показывает текущую сортировку стрелкой (таблица §9, список ситуаций)
 * или не показывает (вид «по монете», design.md решение 2: её заголовок
 * переставляет группы, а не строки под ним, и стрелка читалась бы как
 * сортировка строк). Менять сортировку шапка не умеет ни в одном виде —
 * только переключатель панели фильтров (1.51). Размеченное объединение, а не
 * необязательное поле: потеря `sort` у шапки со стрелкой обязана падать на
 * `tsc`, а не превращаться в шапку без неё молча.
 */
export type DensityTableHeaderProps = { showsSort: true; sort: DensitySort } | { showsSort: false };

/** Строка заголовков §9, общая у таблицы, вида «по монете» и списка ситуаций. */
export function DensityTableHeader(props: DensityTableHeaderProps) {
  return (
    <TableHeader className="sticky top-0 z-10 bg-card">
      <TableRow className="h-6">
        <TableHead className="w-1.5 p-0" />
        {HEADER_COLUMNS.map((column) =>
          props.showsSort && column.sortKey !== undefined ? (
            <SortableHead
              key={column.label}
              label={column.label}
              sortKey={column.sortKey}
              sort={props.sort}
            />
          ) : (
            <PlainHead key={column.label} column={column} />
          ),
        )}
        <TableHead className="h-6 w-[132px] p-0" />
      </TableRow>
    </TableHeader>
  );
}
